import { useEffect, useState } from 'react';
import { getBookingFiles } from '../../lib/api';
import { Clock, UploadSimple } from '@phosphor-icons/react';

/**
 * Pending Upload Alert — admin/owner-only.
 * Lists files where staff_completed=True but processed_at is missing — i.e.
 * staff ne workspace submit kar di hai par final report upload pending hai,
 * so distributor wallet abhi tak debit nahi hua. Until upload is done the
 * booking won't appear in the distributor ledger.
 *
 * Endpoint used: GET /api/booking-files?staff_view=pending-upload (already
 * implemented in booking_files.py line 312).
 */
export default function PendingUploadAlert() {
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await getBookingFiles({ staff_view: 'pending-upload' });
        if (!cancelled) setFiles(data || []);
      } catch {
        if (!cancelled) setFiles([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  if (loading || files.length === 0) return null;

  return (
    <div className="border-2 border-violet-300 bg-violet-50 rounded-lg p-4" data-testid="pending-upload-alert">
      <div className="flex items-center gap-2 mb-2">
        <UploadSimple size={20} weight="fill" className="text-violet-700" />
        <h3 className="text-sm font-bold text-violet-900">
          ⏳ Report Upload Pending — Wallet Blocked ({files.length})
        </h3>
      </div>
      <div className="text-xs text-violet-800 mb-3">
        In files par staff ne booking complete kar di hai par final report upload
        nahi hua. Jab tak upload nahi hota, distributor wallet debit nahi hoga.
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-xs">
          <thead className="bg-violet-100">
            <tr>
              <th className="px-2 py-1 text-left text-violet-900">Distributor</th>
              <th className="px-2 py-1 text-left text-violet-900">File</th>
              <th className="px-2 py-1 text-left text-violet-900">Staff</th>
              <th className="px-2 py-1 text-right text-violet-900">Bookings</th>
              <th className="px-2 py-1 text-left text-violet-900">Completed</th>
            </tr>
          </thead>
          <tbody>
            {files.slice(0, 20).map(f => (
              <tr key={f.id} className="border-t border-violet-200" data-testid={`pending-upload-row-${f.id}`}>
                <td className="px-2 py-1 font-medium">{f.distributor_name}</td>
                <td className="px-2 py-1">{f.original_filename}</td>
                <td className="px-2 py-1">{f.assigned_to_name || '—'}</td>
                <td className="px-2 py-1 text-right">{f.total_bookings_count || 0}</td>
                <td className="px-2 py-1 text-gray-600 inline-flex items-center gap-1">
                  <Clock size={12} />
                  {f.staff_completed_at ? new Date(f.staff_completed_at).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' }) : '—'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {files.length > 20 && (
          <div className="text-[10px] text-violet-600 mt-1">Showing first 20 of {files.length}.</div>
        )}
      </div>
    </div>
  );
}
